import i18n from '../../i18n';
import type { PostType, PostUserTag } from '../../lib/api';
import { formatNumber } from '../../lib/format';

/**
 * Instagram's and Facebook's publishing rules, mirrored from the server's
 * `modules/scheduled-posts/validation.ts` (D-092, D-143) for live counters
 * and inline issues. The server stays the authority and re-checks everything.
 */
export const POST_LIMITS = {
  captionMax: 2200,
  hashtagMax: 30,
  mentionMax: 20,
  collaboratorMax: 3,
  carouselMin: 2,
  carouselMax: 10,
  userTagsPerImage: 20,
  altTextMax: 1000,
  minAspect: 0.8,
  maxAspect: 1.91,
  maxImageBytes: 8_000_000,
  maxVideoBytes: 300_000_000,
  /** A story video (D-136): Meta's reference caps it at 100 MB and 60 seconds. */
  maxStoryVideoBytes: 100_000_000,
} as const;

/** Where a post goes (D-143): a Messenger account is a Facebook Page. */
export type Platform = 'instagram' | 'facebook';

export const platformOf = (channelCode: string): Platform => (channelCode === 'messenger' ? 'facebook' : 'instagram');

export const PLATFORM_NAMES: Record<Platform, string> = { instagram: 'Instagram', facebook: 'Facebook' };

/** A Page's rules (D-143). Meta sets no photo count: Instagram's 10 is kept, so a post shared to both fits both. */
export const FACEBOOK_LIMITS = {
  captionMax: 63_206,
  maxImageBytes: 4_000_000,
  carouselMax: 10,
  reelMaxSeconds: 90,
} as const;

/** The platforms a post reaches (D-143): its account's, and its copy's. */
export interface PostTargets {
  instagram: boolean;
  facebook: boolean;
}

export const INSTAGRAM_ONLY: PostTargets = { instagram: true, facebook: false };

export const USERNAME_RE = /^[a-z0-9._]{1,30}$/;
export const HASHTAG_RE = /^[\p{L}\p{N}_]{1,100}$/u;

export const normalizeUsername = (raw: string) => raw.trim().replace(/^@+/, '').toLowerCase();
export const normalizeHashtag = (raw: string) => raw.trim().replace(/^#+/, '');

export function composeCaption(caption: string, hashtags: string[]): string {
  const body = caption.trim();
  const tags = hashtags.map((h) => `#${h}`).join(' ');
  if (!tags) return body;
  if (!body) return tags;
  return `${body}\n\n${tags}`;
}

export const countHashtags = (text: string) => (text.match(/(^|[^\p{L}\p{N}_&])#[\p{L}\p{N}_]+/gu) ?? []).length;
export const countMentions = (text: string) => (text.match(/(^|[^A-Za-z0-9._@])@[A-Za-z0-9._]+/g) ?? []).length;

export interface ShapeItem {
  kind: 'image' | 'video';
  width: number | null;
  height: number | null;
  userTags: PostUserTag[];
  sizeBytes?: number;
}

export const TYPE_LABELS: Record<PostType, string> = { image: 'Image', carousel: 'Carousel', reel: 'Reel', story: 'Story' };

export interface PostShape {
  type: PostType;
  items: ShapeItem[];
  caption: string;
  hashtags: string[];
  collaborators: string[];
}

/** Reasons the post cannot be published as composed on `platform`; empty = ready. */
export function postIssues(post: PostShape, platform: Platform = 'instagram'): string[] {
  return platform === 'facebook' ? facebookIssues(post) : instagramIssues(post);
}

/** A Page post (D-143): no ratio range, no hashtag cap; multi-photo posts hold photos only. */
function facebookIssues(post: PostShape): string[] {
  const issues: string[] = [];
  const { items } = post;
  if (items.length === 0) {
    issues.push(i18n.t(post.type === 'reel' ? 'postIssues.addReel' : post.type === 'story' ? 'postIssues.addStory' : 'postIssues.addPhoto'));
  } else if (post.type === 'image') {
    if (items.length !== 1 || items[0]?.kind !== 'image') issues.push(i18n.t('postIssues.imageOne'));
  } else if (post.type === 'reel') {
    if (items.length !== 1 || items[0]?.kind !== 'video') issues.push(i18n.t('postIssues.reelOne'));
  } else if (post.type === 'story') {
    if (items.length !== 1) issues.push(i18n.t('postIssues.storyOne'));
  } else {
    if (items.length < POST_LIMITS.carouselMin || items.length > FACEBOOK_LIMITS.carouselMax) {
      issues.push(i18n.t('postIssues.multiPhotoCount', { min: POST_LIMITS.carouselMin, max: FACEBOOK_LIMITS.carouselMax }));
    }
    if (items.some((i) => i.kind === 'video')) issues.push(i18n.t('postIssues.multiPhotoOnly'));
  }
  items.forEach((item, index) => {
    const label = itemLabel(items.length, index);
    if (item.kind === 'image' && (item.sizeBytes ?? 0) > FACEBOOK_LIMITS.maxImageBytes) issues.push(i18n.t('postIssues.pagePhotoSize', { label }));
    if (item.userTags.length > 0) issues.push(i18n.t('postIssues.tagsInstagramOnly', { label }));
  });
  if (post.type === 'story') return issues;
  const caption = composeCaption(post.caption, post.hashtags);
  if (caption.length > FACEBOOK_LIMITS.captionMax) issues.push(i18n.t('postIssues.captionOver', { max: formatNumber(FACEBOOK_LIMITS.captionMax) }));
  if (post.collaborators.length > 0) issues.push(i18n.t('postIssues.collaboratorsInstagram'));
  return issues;
}

/** "Item 2" in a carousel, "The photo" when there is one. */
const itemLabel = (count: number, index: number) => (count > 1 ? i18n.t('postIssues.item', { n: index + 1 }) : i18n.t('postIssues.thePhoto'));

function instagramIssues(post: PostShape): string[] {
  const issues: string[] = [];
  const { items } = post;
  if (items.length === 0) {
    issues.push(i18n.t(post.type === 'reel' ? 'postIssues.addReel' : post.type === 'story' ? 'postIssues.addStory' : 'postIssues.addMedia'));
  } else if (post.type === 'image') {
    if (items.length !== 1 || items[0]?.kind !== 'image') issues.push(i18n.t('postIssues.imageOne'));
  } else if (post.type === 'reel') {
    if (items.length !== 1 || items[0]?.kind !== 'video') issues.push(i18n.t('postIssues.reelOne'));
  } else if (post.type === 'story') {
    if (items.length !== 1) issues.push(i18n.t('postIssues.storyOne'));
    if (items.some((i) => i.kind === 'video' && (i.sizeBytes ?? 0) > POST_LIMITS.maxStoryVideoBytes)) {
      issues.push(i18n.t('postIssues.storySize'));
    }
  } else if (items.length < POST_LIMITS.carouselMin || items.length > POST_LIMITS.carouselMax) {
    issues.push(i18n.t('postIssues.carouselCount', { min: POST_LIMITS.carouselMin, max: POST_LIMITS.carouselMax }));
  }
  // Reels and stories are full-screen: no ratio range, and no one tagged on them.
  const isFeed = post.type === 'image' || post.type === 'carousel';
  items.forEach((item, index) => {
    const label = itemLabel(items.length, index);
    if (item.userTags.length > 0 && (item.kind === 'video' || !isFeed)) {
      issues.push(i18n.t('postIssues.tagsPhotosOnly', { label }));
    }
    if (item.kind === 'image' && isFeed && item.width && item.height) {
      const ratio = item.width / item.height;
      if (ratio < POST_LIMITS.minAspect - 0.005 || ratio > POST_LIMITS.maxAspect + 0.005) {
        issues.push(i18n.t('postIssues.ratio', { label }));
      }
    }
  });
  // A story sends none of this (`toInput` blanks it), so a draft's leftover caption cannot block it.
  if (post.type === 'story') return issues;
  const caption = composeCaption(post.caption, post.hashtags);
  if (caption.length > POST_LIMITS.captionMax) issues.push(i18n.t('postIssues.captionOver', { max: formatNumber(POST_LIMITS.captionMax) }));
  if (countHashtags(caption) > POST_LIMITS.hashtagMax) issues.push(i18n.t('postIssues.hashtags', { max: POST_LIMITS.hashtagMax }));
  if (countMentions(caption) > POST_LIMITS.mentionMax) issues.push(i18n.t('postIssues.mentions', { max: POST_LIMITS.mentionMax }));
  if (post.collaborators.length > POST_LIMITS.collaboratorMax) {
    issues.push(i18n.t('postIssues.collaborators', { max: POST_LIMITS.collaboratorMax }));
  }
  return issues;
}
